import {lazy, Suspense, useEffect} from 'react';
import {Helmet} from 'react-helmet-async';
import {Link} from 'react-router-dom';

//Components
import Bubbles, { BubblesType } from '~components/Bubbles';
import Loader from '~components/Loader';
import LandingLink from '~components/LandingLink';
//Icons
import LogoIcon from '~icons/LogoIcon';
//Hooks
import useAuthCallbacks from '~hooks/useAuthCallbacks';
import {useAuthContext} from '~context/auth';
//Config
import {forgotPasswordRoute, registerChooseTypeRoute} from '~config/routes';
// Utils
import {chunkErrorHandler, cleanUp} from '~utils/chunkErrorHandler';
// Types
import {AuthLoadingState} from '~context/auth/types';



const LinkedInAuthBtn =  lazy(() => import('~components/LinkedInAuthBtn').then(cleanUp).catch(chunkErrorHandler));
const GoogleAuthBtn =  lazy(() => import('~components/GoogleAuthBtn').then(cleanUp).catch(chunkErrorHandler));
const LoginForm =  lazy(() => import('~pages/Login/LoginForm').then(cleanUp).catch(chunkErrorHandler));

export default function Login() {
    const {onLoginSuccess, onSocialLoginError, redirectByProfileType} = useAuthCallbacks();
    const {loading, profile} = useAuthContext();

    useEffect(() => {
        if (profile) {
            redirectByProfileType(profile);
        }
    }, [profile, redirectByProfileType]);

    return (
        <div className="login-page auth-page">
            <Helmet>
                <title>Login | Human Resources Management, Job Finder, Recruitment</title>
                <meta
                    name="description"
                    content="Focusing on Talent Sourcing and Screening to optimize quality-of-hire and time-to-hire. Providing job seekers unparalleled opportunity to stand out!"
                />
            </Helmet>

            <LandingLink className="auth-page__logo">
                <LogoIcon/>
            </LandingLink>

            <div className="auth-page__content">
                <h1 className="auth-page__title">Login</h1>

                <Suspense fallback={<div className="auth-page__form--fallback"/>}>
                    <LoginForm onSuccess={onLoginSuccess}/>
                </Suspense>

                <Link to={forgotPasswordRoute} className="auth-page__link">
                    Forgot your password?
                </Link>

                <Suspense fallback="">
                    <h2 className="auth-page__social-signin-title">Login option for Talent Users:</h2>

                    <LinkedInAuthBtn
                        onAuthFail={onSocialLoginError}
                        onAuthSuccess={onLoginSuccess}
                        title="Sign in with LinkedIn"
                        className="auth-page__social-signin"
                    />
                    <GoogleAuthBtn
                        onAuthFail={onSocialLoginError}
                        onAuthSuccess={onLoginSuccess}
                        title="Sign in with Google"
                        actionType="SIGN_IN"
                        className="auth-page__social-signin"
                    />

                    <p className="auth-page__login-link">
                        Don't have an account?{' '}
                        <Link to={registerChooseTypeRoute}>Sign up</Link>
                    </p>
                </Suspense>
            </div>

            <Bubbles type={BubblesType.SIMPLE}/>

            {loading === AuthLoadingState.PENDING && <Loader withSpinner solidBackdrop/>}
        </div>
    );
}
